import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
export function Hero() {
  return <section className="pt-6 pb-8 px-4 relative overflow-hidden" data-id="element-130">
      <div className="max-w-md sm:max-w-lg mx-auto text-center relative z-10" data-id="element-131">
        {/* Attention pill */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="inline-flex items-center gap-2 bg-brand-card border border-brand-danger/40 px-4 py-1.5 rounded-full mb-5" data-id="element-132">
          <span className="w-2 h-2 rounded-full bg-brand-danger animate-pulse" data-id="element-133" />
          <span className="text-[11px] sm:text-xs font-black tracking-wider text-brand-danger uppercase leading-tight" data-id="element-134">
            ACHTUNG: SCHWEIZER SKI-FANS, DIE ES SATT HABEN, JEDES WOCHENENDE ALS
            "TOURISTEN-MELKKUH" BEHANDELT ZU WERDEN
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="text-[30px] sm:text-5xl font-black text-brand-cream leading-[1.05] mb-5" data-id="element-135">
          Planen Sie Ihre gesamte{' '}
          <span className="text-brand-orange underline decoration-brand-orange/40 underline-offset-4" data-id="element-136">
            Skisaison 2026
          </span>{' '}
          in nur <span className="text-brand-gold" data-id="element-137">5 Minuten</span>.
        </motion.h1>

        {/* Subheadline card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-brand-card border-l-4 border-brand-orange rounded-r-lg px-4 py-3 mb-6 text-left" data-id="element-138">
          <p className="text-sm sm:text-base text-gray-300 leading-snug" data-id="element-139">
            Das erste{' '}
            <span className="text-brand-orange font-bold" data-id="element-140">"Apri e Scia"</span>{' '}
            System mit 20 Geheimstationen, SBB-Hacks und Insider-Wissen.{' '}
            <span className="text-brand-cream font-bold" data-id="element-141">
              Holen Sie sich Ihre Wochenenden zurück.
            </span>
          </p>
        </motion.div>

        {/* Product image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15
      }} className="mb-6" data-id="element-142">
          <a href="#pricing" className="block cursor-pointer" data-id="element-143">
            <img src="https://cdn.magicpatterns.com/uploads/65hK9jx5zEFDzyRLLZshXr/mockups_guia_alpina_.png" alt="Swiss Peak System 2026" className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-144" />
          </a>
          <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-2" data-id="element-145">
            Swiss Peak System 2026
          </p>
        </motion.div>

        {/* Star rating */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="flex items-center justify-center gap-2 mb-3" data-id="element-146">
          <div className="flex" data-id="element-147">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-brand-gold fill-current drop-shadow-[0_0_6px_rgba(245,245,245,0.6)]" data-id="element-148" />)}
          </div>
          <span className="text-sm font-bold text-brand-cream" data-id="element-149">
            4.9/5 (+500 verifizierte Bewertungen)
          </span>
        </motion.div>

        {/* Scarcity pill */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.25
      }} className="inline-flex items-center gap-2 border border-brand-orange/40 bg-brand-orange/5 px-4 py-1.5 rounded-full mb-5" data-id="element-150">
          <span className="text-sm font-bold text-brand-orange" data-id="element-151">
            🔥 47 Skifahrer haben sich heute für dieses System entschieden
          </span>
        </motion.div>

        {/* Main CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-152">
          <CTAButton className="w-full" data-id="element-153">
            JA! ICH WILL MEINE FREIHEIT ZURÜCK
          </CTAButton>
        </motion.div>

        {/* Trust line */}
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 mt-5 text-xs text-gray-400 font-medium" data-id="element-154">
          <span data-id="element-155">⚡ Sofortiger Zugriff</span>
          <span className="text-gray-600" data-id="element-156">•</span>
          <span data-id="element-157">🛡️ 7 Tage Garantie</span>
          <span className="text-gray-600" data-id="element-158">•</span>
          <span data-id="element-159">💳 Einmalzahlung</span>
        </div>

        {/* Country/Resource line */}
        <div className="mt-5 flex items-center justify-center gap-2" data-id="element-160">
          <div className="flex gap-1" data-id="element-161">
            <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20 shadow" data-id="element-162">
              <img src="https://flagcdn.com/w80/ch.png" alt="Schweiz" className="w-full h-full object-cover" data-id="element-163" />
            </div>
            <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20 shadow" data-id="element-164">
              <img src="https://flagcdn.com/w80/de.png" alt="Deutschland" className="w-full h-full object-cover" data-id="element-165" />
            </div>
            <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20 shadow" data-id="element-166">
              <img src="https://flagcdn.com/w80/at.png" alt="Österreich" className="w-full h-full object-cover" data-id="element-167" />
            </div>
          </div>
          <p className="text-xs text-gray-400 font-medium" data-id="element-168">
            Die Nr. 1 Ressource für intelligente Skifahrer in der Schweiz.
          </p>
        </div>
      </div>
    </section>;
}